import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  Users,
  Calendar,
  ClipboardList,
  CheckSquare,
  FileText,
  DollarSign,
  Settings,
  LogOut,
  X,
  Plus,
  ClipboardCheck,
  AlertTriangle,
  Shield,
  Activity,
  Stethoscope,
  GitBranch,
  LockKeyhole,
  KeyRound,
  Calculator,
  MapPin,
  Route,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/core/utils';
import { useAuth, usePermissions } from '@/core/hooks';
import { Button } from '@/core/components';
import type { Role } from '@/core/types/auth';

export interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permission?: string;
  requiredRole?: Role;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: <Home className="h-5 w-5" /> },
  {
    label: 'Clients',
    path: '/clients',
    icon: <Users className="h-5 w-5" />,
    permission: 'clients:read',
  },
  {
    label: 'Caregivers',
    path: '/caregivers',
    icon: <Users className="h-5 w-5" />,
    permission: 'caregivers:read',
  },
  {
    label: 'Scheduling',
    path: '/scheduling',
    icon: <Calendar className="h-5 w-5" />,
    permission: 'visits:read',
  },
  {
    label: 'Calendar View',
    path: '/scheduling/calendar',
    icon: <Calendar className="h-5 w-5" />,
    permission: 'visits:read',
  },
  {
    label: 'Schedule Optimizer',
    path: '/schedule-optimizer',
    icon: <Route className="h-5 w-5" />,
    permission: 'admin:access',
  },
  {
    label: 'Care Plans',
    path: '/care-plans',
    icon: <ClipboardList className="h-5 w-5" />,
    permission: 'care_plans:read',
  },
  {
    label: 'Create Care Plan',
    path: '/care-plans/new',
    icon: <Plus className="h-5 w-5" />,
    permission: 'care_plans:create',
  },
  {
    label: 'Tasks',
    path: '/tasks',
    icon: <CheckSquare className="h-5 w-5" />,
    permission: 'tasks:read',
  },
  {
    label: 'Time Tracking',
    path: '/time-tracking',
    icon: <FileText className="h-5 w-5" />,
    permission: 'evv:read',
  },
  {
    label: 'Billing',
    path: '/billing',
    icon: <DollarSign className="h-5 w-5" />,
    permission: 'billing:read',
  },
  {
    label: 'Carer Pay',
    path: '/carer-payroll',
    icon: <Calculator className="h-5 w-5" />,
    permission: 'admin:access',
  },
  {
    label: 'Live Carers',
    path: '/live-carers',
    icon: <MapPin className="h-5 w-5" />,
    permission: 'admin:access',
  },
  {
    label: 'Admin AI',
    path: '/admin-ai',
    icon: <Sparkles className="h-5 w-5" />,
    permission: 'admin:access',
  },
  {
    label: 'API Keys',
    path: '/integration-keys',
    icon: <KeyRound className="h-5 w-5" />,
    permission: 'admin:access',
  },
  {
    label: 'Incidents',
    path: '/incidents',
    icon: <AlertTriangle className="h-5 w-5" />,
    permission: 'incidents:read',
  },
  {
    label: 'Quality Assurance',
    path: '/quality-assurance',
    icon: <ClipboardCheck className="h-5 w-5" />,
    permission: 'audits:view',
  },
  {
    label: 'Compliance',
    path: '/compliance',
    icon: <Shield className="h-5 w-5" />,
    permission: 'compliance:read',
  },
  {
    label: 'Admin',
    path: '/admin',
    icon: <Settings className="h-5 w-5" />,
    permission: 'admin:access',
  },
  {
    label: 'Readiness',
    path: '/enterprise-readiness',
    icon: <Activity className="h-5 w-5" />,
    permission: 'admin:access',
    requiredRole: 'SUPER_ADMIN',
  },
  {
    label: 'Workflows',
    path: '/workflow-builder',
    icon: <GitBranch className="h-5 w-5" />,
    permission: 'admin:access',
    requiredRole: 'SUPER_ADMIN',
  },
  {
    label: 'GP Connect',
    path: '/gp-connect',
    icon: <Stethoscope className="h-5 w-5" />,
    permission: 'admin:access',
    requiredRole: 'SUPER_ADMIN',
  },
  {
    label: 'Security',
    path: '/security-enterprise',
    icon: <LockKeyhole className="h-5 w-5" />,
    permission: 'admin:access',
    requiredRole: 'SUPER_ADMIN',
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { logout, user } = useAuth();
  const { can } = usePermissions();

  const filteredNavItems = navItems.filter((item) => {
    if (item.path === '/') return true;
    
    // Check permission if specified
    if (item.permission && !can(item.permission)) return false;

    if (item.requiredRole && user && !user.roles.includes(item.requiredRole)) return false;
    
    // Hide admin section from non-admin users
    if (item.path === '/admin' && user) {
      return user.roles.includes('SUPER_ADMIN') || user.roles.includes('ADMIN');
    }
    
    return true;
  });

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200',
          'transform transition-transform duration-200 ease-in-out lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Close button (mobile only) */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 lg:hidden">
            <h2 className="text-lg font-semibold">Menu</h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => {
              // Add IDs for tour targeting
              let id: string | undefined;
              if (item.path === '/clients') id = 'clients-nav';
              if (item.path === '/scheduling') id = 'scheduling';
              if (item.path === '/care-plans') id = 'care-plan';
              if (item.path === '/tasks') id = 'tasks';
              if (item.path === '/time-tracking') id = 'evv-status';

              return (
                <NavLink
                  key={item.path}
                  id={id}
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    )
                  }
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="border-t border-gray-200 p-3 space-y-1">
            <NavLink
              to="/settings"
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                )
              }
            >
              <Settings className="h-5 w-5" />
              Settings
            </NavLink>
            <button
              onClick={() => {
                logout();
                onClose();
              }}
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 w-full transition-colors"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};
